import { setUserIconColor, showModal, StatBubble } from 'components/AppShell';
import { Icon } from 'components/Icon';
import { signOut } from 'components/Login';
import { appendChild, type JSXChild, type JSXProps, render } from 'engine/index';
import { Check, ChevronLeft, ChevronRight, Hourglass, Trophy, X } from 'lucide-static';
import {
	vingerAfdrukkenPlayerColors,
	type Card,
	type VingerAfdruk,
	type VingerAfdrukkenAnswer,
	type VingerAfdrukkenClient,
	type VingerAfdrukkenUpdate,
} from 'shared/modelTypes/vinger-afdrukken';
import { delayMs, lateinit, onUnmount, unreachable } from 'shared/util';
import { Game } from './game';
import { rpc, State } from 'engine/rpc';
import { Timer } from 'components/Timer';
import { BottomRight } from 'components/BottomRight';
import { marked } from 'marked';

export class VingerAfdrukken extends Game<'vinger-afdrukken-spel'> {
	override maxPlayers: number = 4;

	view: HTMLElement = lateinit;
	scoreEl: HTMLElement = lateinit;

	answers: { answer: string; questionNumber: number }[] = [];

	override getGameName(): string {
		return 'Vingerafdrukken Spel';
	}

	override defineBottomUI(): JSXChild {
		return (
			<>
				<StatBubble color="green" label="Punten" value="0" id="score" />
			</>
		);
	}
	override onShellCreated(view: HTMLElement): void {
		this.view = view;
		super.onShellCreated(view);
		this.scoreEl = view.querySelector('#score')!;
	}

	override handleUpdate(update: VingerAfdrukkenUpdate): void {
		const sessionId = State.gameId.toString();
		const playerId = State.playerId;
		const kind = update.kind;
		// const playerId = State.playerId;

		// console.log('message', update);

		switch (kind) {
			case 'trigger-effect':
				this.triggerEffect(update);
				break;
			case 'show-login-screen':
				this.showLoginScreen();
				break;
			case 'show-register-screen':
				this.showRegisterScreen('team', vingerAfdrukkenPlayerColors);
				break;
			case 'update-players-state':
				this.updatePlayersState(update);
				break;
			case 'update-player-state':
				State.name = update.player.name;
				State.color = update.player.color;
				State.score = update.player.score;
				this.answers = update.player.answers;

				this.shellNameEl.innerText = update.player.name;
				setUserIconColor(this.userIconEl, update.player.color);
				this.scoreEl.innerText = (State.score ?? 0).toString();
				break;
			case 'show-questions-screen':
				{
					render(
						this.shellContentEl,
						<QuestionsScreen
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
							// title={title}
							vingerAfdrukken={update.vingerafdrukken}
							answers={this.answers}
							onAnswer={(answer) => {
								rpc.sendVingerAfdrukkenMessage.query({
									sessionId,
									update: {
										kind: 'submit-answer',
										playerId,
										...answer,
									},
								});
							}}
						/>,
					);
				}
				break;
			case 'show-wait-screen':
				{
					render(this.shellContentEl, <WaitScreen title={update.title} message={update.message} />);
				}
				break;
			case 'show-wait-screen-and-question-screen':
				{
					render(
						this.shellContentEl,
						<WaitScreenAndQuestions title={update.title} message={update.message} vingerAfdrukken={update.vingerafdrukken} />,
					);
				}
				break;

			case 'show-choose-diceroll-screen':
				{
					render(
						this.shellContentEl,
						<ChooseDiceroll
							onChoose={(diceroll) => {
								rpc.sendVingerAfdrukkenMessage.query({
									sessionId,
									update: {
										kind: 'submit-diceroll',
										playerId,
										diceroll,
									},
								});
							}}
						/>,
					);
				}
				break;
			case 'show-choose-question-screen':
				{
					render(
						this.shellContentEl,
						<ChooseAnswerToPresent
							vingerAfdrukken={update.vingerafdrukken}
							microvezelPower={update.microvezelPower}
							onChoose={(questionNumber) => {
								rpc.sendVingerAfdrukkenMessage.query({
									sessionId,
									update: {
										kind: 'submit-answer-to-present',
										playerId,
										questionNumber,
									},
								});
							}}
						/>,
					);
				}
				break;
			case 'show-present-answer-screen':
				{
					render(
						this.shellContentEl,
						<PresentAnswer
							question={update.question}
							answer={update.answer}
							number={update.number}
							onChoose={() => {
								rpc.sendVingerAfdrukkenMessage.query({
									sessionId,
									update: {
										kind: 'finish-presenting',
									},
								});
							}}
						/>,
					);
				}
				break;
			case 'show-results-screen':
				render(this.shellContentEl, <ResultsScreen results={update.players} reportUrl={update.reportUrl} />);
				break;
			case 'show-vinger-afdruk-completed':
				appendChild(this.view, <ShowVingerAfdrukCompleted vingerAfdruk={update.vingerAfdruk} type={update.type} />);
				break;
			case 'show-swipecards-screen':
				{
					render(
						this.shellContentEl,
						<ChooseCards
							cards={update.cards}
							onChosen={(chosen) => {
								rpc.sendVingerAfdrukkenMessage.query({
									sessionId,
									update: {
										kind: 'choose-cards',
										playerId,
										cards: chosen,
									},
								});
								render(
									this.shellContentEl,
									<WaitScreen
										title="Geen onderwerpen om te beoordelen."
										message="Wacht tot andere spelers de onderwerpen beoordeeld hebben."
									/>,
								);
							}}
						/>,
					);
				}
				break;

			default:
				unreachable(kind);
		}
	}
}

export function QuestionsScreen({
	vingerAfdrukken,
	onAnswer,
	answers,
	timerFrom,
	timerTo,
}: {
	timerFrom: number;
	timerTo: number;
	vingerAfdrukken: VingerAfdrukkenClient;
	answers: { answer: string; questionNumber: number }[];
	onAnswer: (answer: VingerAfdrukkenAnswer) => void;
}) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-balance text-center text-2xl font-bold leading-tight text-gray-900">
				De vragen! Jullie krijgen 45 minuten de tijd om de antwoorden op te schrijven.
			</h2>
			<p class="mb-6 text-gray-700">Tip: gebruik praktische voorbeelden</p>
			<div class="flex w-full flex-col gap-5 sm:flex-row">
				{vingerAfdrukken.map((afdruk, i) => {
					const afdrukColors = [
						//
						'bg-blue-50 text-blue-950 border-blue-200 shadow-blue-200/50',
						'bg-purple-50 text-purple-950 border-purple-200 shadow-purple-200/50',
						'bg-green-50 text-green-950 border-green-200 shadow-green-200/50',
					][i];

					const pointsColors = [
						//
						'text-blue-800',
						'text-purple-800',
						'text-green-900',
					][i];

					const questionColors = [
						//
						'bg-blue-300 text-blue-950',
						'bg-purple-300 text-purple-950',
						'bg-green-300 text-green-950',
					][i];

					return (
						<div class={'flex-1 shrink-0 rounded-lg border p-4 shadow-md ' + afdrukColors}>
							<h3 class="block text-center text-xl font-bold leading-none">{`Afdruk ${afdruk.number}`}</h3>
							<small class={'mb-4 block text-center text-base ' + pointsColors}>{`${afdruk.points} punten`}</small>
							<div class="flex flex-col gap-3">
								{afdruk.questions.map((q) => {
									const questionButton: HTMLElement = (
										<button
											on:click={() =>
												showModal((confirm, cancel) => {
													let answer = answers.find((a) => a.questionNumber == q.number);
													if (!answer) {
														answer = {
															questionNumber: q.number,
															answer: '',
														};
														answers.push(answer);
													}

													const formModal: HTMLElement = (
														<div class="rounded-lg bg-white p-6">
															<h4 class="mb-2 text-lg font-bold text-gray-900">{`Vraag ${q.number}`}</h4>
															<div
																class="max-w-[50ch] text-pretty text-gray-700"
																__innerHTML={marked(q.text)}
															></div>

															<form
																on:submit={(e: SubmitEvent) => {
																	e.preventDefault();
																	const form = e.target as HTMLFormElement;
																	const data = new FormData(form);
																	const answerText = ((data.get('answer') ?? '') as string).trim();
																	answer.answer = answerText;
																	onAnswer({
																		answer: answerText,
																		questionNumber: q.number,
																	});
																	if (answerText && answerText.length) {
																		questionButton.setAttribute('data-done', '');
																	} else {
																		questionButton.removeAttribute('data-done');
																	}
																	confirm();
																}}
															>
																<div class="py-6">
																	<label class="font-semibold text-gray-800">Jouw Antwoord</label>
																	<textarea
																		name="answer"
																		rows="5"
																		placeholder="Antwoord"
																		class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
																	>
																		{answer?.answer}
																	</textarea>
																</div>
																<div class="flex justify-between">
																	<button on:click={cancel} type="button" class="p-3 text-gray-600">
																		Annuleren
																	</button>
																	<button type="submit" class="rounded-md bg-theme-600 p-3 text-theme-50">
																		Opslaan
																	</button>
																</div>
															</form>
														</div>
													);
													return formModal;
												})
											}
											data-done={answers.find((a) => a.questionNumber == q.number)?.answer ? true : undefined}
											class={'group relative block rounded-md p-3 text-center ' + questionColors}
										>
											{`Vraag ${q.number}`}

											<div class="absolute -right-1 -top-1 z-[1] grid size-6 place-items-center rounded-full border-b border-gray-300 bg-white opacity-0 shadow-sm group-data-[done]:opacity-100">
												<Icon icon={Check} class="size-5 text-green-600" />
											</div>
										</button>
									);

									return questionButton;
								})}
							</div>
						</div>
					);
				})}
			</div>

			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function WaitScreen({ title, message }: { title: string; message: string }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">{title}</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{message}</pre>
			<Icon icon={Hourglass} class="size-20 text-gray-300" />
		</div>
	);
}

export function WaitScreenAndQuestions({
	title,
	message,
	vingerAfdrukken,
}: {
	title: string;
	message: string;
	vingerAfdrukken: VingerAfdrukkenClient;
}) {
	const afdrukColors = [
		//
		'bg-blue-300 text-blue-950 border-blue-200 shadow-blue-200/50',
		'bg-purple-300 text-purple-950 border-purple-200 shadow-purple-200/50',
		'bg-green-300 text-green-950 border-green-200 shadow-green-200/50',
	];

	return (
		<div class="flex h-fit w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">{title}</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{message}</pre>
			<div class="mt-8 grid w-full grid-cols-3 gap-5">
				{vingerAfdrukken.map((v, i) => {
					const j = Math.floor(i);
					return v.questions.map((q) => {
						return (
							<button
								on:click={() =>
									showModal((_confirm, cancel) => {
										const view = (
											<div class="rounded-lg bg-white p-6">
												<h3 class="text-lg font-bold">Vraag {q.number}</h3>
												<pre
													class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800"
													__innerHTML={marked(q.text)}
												></pre>
												<h3 class="text-lg font-bold">Jouw antwoord</h3>
												<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">
													{q.answer ? q.answer : 'Geen antwoord ingevoerd'}
												</pre>
												<div class="flex justify-between">
													<button on:click={cancel} type="button" class="p-3 text-gray-600">
														Annuleren
													</button>
												</div>
											</div>
										);
										return view;
									})
								}
								class={'aspect-[2/1] size-full rounded-md border px-1 py-2 text-xl shadow-md ' + afdrukColors[j]}
								style={q.capturable || !q.locked ? '' : 'opacity: 0.2'}
							>
								{q.number}
							</button>
						);
					});
				})}
			</div>
		</div>
	);
}

export function ChooseDiceroll({ onChoose }: { onChoose: (roll: number) => void }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">Jullie zijn aan zet!</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">Hoeveel ogen hebben jullie gegooid?</pre>
			<div class="mt-8 grid w-full grid-cols-3 gap-5">
				{[1, 2, 3, 4, 5, 6].map((v) => (
					<button
						on:click={() => onChoose(v)}
						class="aspect-square size-full rounded-md border border-gray-900 text-xl text-gray-900 shadow-md"
					>
						{v}
					</button>
				))}
			</div>
		</div>
	);
}

export function ChooseAnswerToPresent({
	vingerAfdrukken,
	microvezelPower,
	onChoose,
}: {
	vingerAfdrukken: VingerAfdrukkenClient;
	microvezelPower: boolean;
	onChoose: (roll: number) => void;
}) {
	const afdrukColors = [
		//
		'bg-blue-300 text-blue-950 border-blue-200 shadow-blue-200/50',
		'bg-purple-300 text-purple-950 border-purple-200 shadow-purple-200/50',
		'bg-green-300 text-green-950 border-green-200 shadow-green-200/50',
	];

	return (
		<div class="flex h-fit w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">Jullie zijn aan zet!</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">
				Welke vraag en antwoord wil je presenteren?
				{microvezelPower && (
					<span class="font-bold">
						<br /> Gefeliciteerd! Jullie hebben Microvezel power te pekken. Je mag ook vragen presenteren die al op het bord
						liggen.
					</span>
				)}
				{!microvezelPower && (
					<small class="text-gray-700">
						<br />
						Je mag alleen vragen presenteren die nog niet op het bord liggen.
					</small>
				)}
			</pre>
			<div class="mt-8 grid w-full grid-cols-3 gap-5">
				{vingerAfdrukken.map((v, i) => {
					const j = Math.floor(i);
					return v.questions.map((q) => {
						return (
							<button
								on:click={() =>
									(microvezelPower && q.capturable) || !q.locked
										? showModal((confirm, cancel) => {
												const view = (
													<div class="rounded-lg bg-white p-6">
														<h3 class="text-lg font-bold">Vraag {q.number}</h3>
														<pre
															class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800"
															__innerHTML={marked(q.text)}
														></pre>
														<h3 class="text-lg font-bold">Jouw antwoord</h3>
														<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">
															{q.answer ? q.answer : 'Geen antwoord ingevoerd'}
														</pre>
														<div class="flex justify-between">
															<button on:click={cancel} type="button" class="p-3 text-gray-600">
																Annuleren
															</button>
															<button
																on:click={() => {
																	confirm();
																	onChoose(q.number);
																}}
																class="rounded-md bg-theme-600 p-3 text-theme-50"
															>
																Kies dit topic
															</button>
														</div>
													</div>
												);
												return view;
											})
										: undefined
								}
								class={'aspect-[2/1] size-full rounded-md border px-1 py-2 text-xl shadow-md ' + afdrukColors[j]}
								style={(microvezelPower && q.capturable) || !q.locked ? '' : 'opacity: 0.2'}
							>
								{q.number}
							</button>
						);
					});
				})}
			</div>
		</div>
	);
}

export function PresentAnswer({
	number,
	question,
	answer,
	onChoose,
}: {
	question: string;
	answer: string;
	number: number;
	onChoose: () => void;
}) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">Presentatie</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">Presenteer jouw vraag aan de groep</pre>

			<h3 class="text-lg font-bold">Vraag {number}</h3>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800" __innerHTML={marked(question)}></pre>
			<h3 class="text-lg font-bold">Jouw antwoord</h3>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{answer}</pre>
			<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={onChoose}>
				Klaar
			</button>
		</div>
	);
}

export function ResultsScreen({ results, reportUrl }: { results: { name: string; color: string; score: number }[]; reportUrl: string }) {
	return (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">Einduitslag</h2>
			<table class="overflow-hidden rounded-md shadow-sm">
				<thead>
					<tr class="bg-gray-100">
						<th class="px-5 py-1">Rank</th>
						<th class="px-5 py-1">Pion</th>
						<th class="px-5 py-1">Naam</th>
						<th class="px-5 py-1">Punten</th>
					</tr>
				</thead>
				<tbody>
					{results.map((r, i) => {
						const highlight = State.color == r.color;

						return (
							<tr class={highlight ? 'bg-yellow-100 ring-2 ring-yellow-400' : 'even:bg-gray-50'}>
								<td class="px-5 py-1">
									<div class="inline-flex items-baseline gap-3">
										<span class="font-bold">{i + 1}</span>
										{i == 0 && <Icon icon={Trophy} class="size-4 text-orange-400" />}
										{i == 1 && <Icon icon={Trophy} class="size-4 text-gray-400" />}
										{i == 2 && <Icon icon={Trophy} class="size-4 text-orange-700" />}
										{i >= 3 && <Icon icon={Trophy} class="size-4 opacity-0" />}
									</div>
								</td>
								<td class="px-5 py-1">{r.color}</td>
								<td class="px-5 py-1">{r.name}</td>
								<td class="px-5 py-1">{r.score}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<div class="flex gap-3">
				{reportUrl && (
					<button
						class="rounded-md bg-theme-600 p-4 text-theme-50"
						on:click={() => {
							const url = new URL(reportUrl, window.location.origin);
							const link = document.createElement('a');
							link.href = url.toString();
							link.download = reportUrl.split('/').pop() || 'download';
							document.body.appendChild(link);
							link.click();
							document.body.removeChild(link);
						}}
					>
						Rapport downloaden
					</button>
				)}
				<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={signOut}>
					Spel afsluiten
				</button>
			</div>
		</div>
	);
}

export function ShowVingerAfdrukCompleted({ vingerAfdruk, type }: { vingerAfdruk: VingerAfdruk; type: 'completed' | 'stolen' }) {
	let message = 'Vingerafdruk verroverd';
	switch (type) {
		case 'completed':
			message = `Gefeliciteerd! Je hebt een vingerafdruk veroverd. Je ontvangt ${vingerAfdruk.points} punten.`;
			break;
		case 'stolen':
			message = `Helaas je hebt een vingerafdruk verloren! Je verliest ${vingerAfdruk.points} punten.`;
			break;
	}

	const view = (
		<div
			rate-conclusion
			class="absolute bottom-10 left-1/2 flex w-4/5 -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg"
			// class="absolute bottom-28 left-1/2 flex w-4/5 flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg"
		>
			<button
				action-cancel
				class="absolute right-2 top-2 rounded-md p-2"
				on:click={(event: MouseEvent) => {
					const btn = event.target as HTMLElement;
					const rateConclusionPopUp = btn.closest('[rate-conclusion]');
					if (rateConclusionPopUp) {
						rateConclusionPopUp.remove();
					}
				}}
			>
				<Icon icon={X} class="size-6" />
			</button>
			<h3 class="mb mb-2 text-lg font-bold text-gray-900">Vingerafdruk {vingerAfdruk.number}</h3>
			<p class="w-full p-1 text-center">{message}</p>
		</div>
	);

	return view;
}

export function ChooseCards({ cards, onChosen }: { cards: Card[]; onChosen: (chosen: number[]) => void }) {
	const chosen: number[] = [];
	let swipeCount = 0;

	const DragCard = (props: JSXProps, _children: JSXChild[]) => {
		const view: HTMLElement = (
			<div
				card
				data-value={props.card.questionNumber}
				class="w-60 max-w-full transition-transform duration-500"
				style={`--i:${props.index}; grid-area: 1 / 1; z-index:${80 - props.index};`}
			>
				<div
					style={`transform:translateY(calc(var(--i)*4px)); transition-duration:calc(100ms + 200ms * var(--i))`}
					class="relative w-full rounded-md border border-gray-900 bg-white p-6 text-center text-sm shadow-md transition-transform md:text-base"
				>
					<img src="/logo.svg" class="mb-4 size-24 w-full border-b-2 pb-4" />
					<h3 class="text-lg font-bold">Vraag {props.card.questionNumber}</h3>
					<pre
						class="line-clamp-5 h-28 w-full text-ellipsis whitespace-normal py-2 text-center font-sans text-gray-800 md:h-32"
						__innerHTML={marked(props.card.question)}
					></pre>
					<h3 class="text-lg font-bold">Antwoord</h3>
					<pre class="line-clamp-5 h-28 w-full text-ellipsis whitespace-normal py-2 text-center font-sans text-gray-800 md:h-32">
						{props.card.answer
							? props.card.answer.slice(0, 160) + (props.card.answer.length > 120 ? '...' : '')
							: 'Geen antwoord ingevoerd'}
					</pre>
				</div>
			</div>
		);
		return view;
	};

	const view: HTMLElement = (
		<div class="flex w-fit max-w-screen-lg select-none flex-col p-6">
			<div class="mb-4 flex flex-row items-center gap-4">
				<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">Beoordeel elkaars ideeën</h2>
			</div>
			<div class="flex items-center">
				<Icon icon={ChevronLeft} class="size-10 text-gray-200" />
				<div card-container class="grid grow place-items-center">
					{cards.map((card, i) => {
						return <DragCard card={card} index={i} />;
					})}
				</div>
				<Icon icon={ChevronRight} class="size-10 text-gray-200" />
			</div>
			<div class="mt-14 flex flex-1 shrink items-center justify-between">
				<button
					on:click={() => {
						cardLeft();
					}}
					class="z-[90] flex aspect-square size-20 items-center justify-center rounded-lg border border-red-600 bg-red-500 p-3 text-center text-xs text-red-50"
				>
					<Icon icon={X} class="size-10 text-white" />
				</button>

				<button
					on:click={() => {
						cardRight();
					}}
					class="z-[90] flex aspect-square size-20 items-center justify-center rounded-lg border border-green-600 bg-green-500 p-3 text-center text-xs text-green-50"
				>
					<Icon icon={Check} class="size-10 text-white" />
				</button>
			</div>
		</div>
	);
	const cardContainer = view.querySelector('[card-container]') as HTMLElement;

	let startX: number, startTime: number;

	const grabCard = () => {
		return cardContainer.querySelector('[card]:not([dragged])') as HTMLElement;
	};

	const cleanupCard = async (card: HTMLElement) => {
		const remainingCards = cardContainer.querySelectorAll('[card]:not([dragged])') as NodeListOf<HTMLElement>;
		let i = 0;
		for (const card of remainingCards) {
			card.style.setProperty('--i', i.toString());
			i++;
		}

		await delayMs(300);

		await card.animate([{ opacity: '1' }, { opacity: '0' }], {
			easing: 'ease-out',
			duration: 300,
		}).finished;

		card.remove();
	};

	const cardLeft = () => {
		const card = grabCard();
		if (!card) return;

		const val = card.getAttribute('data-value');
		if (val) {
			// chosen.left.push(val);
			swipeCount++;
			if (swipeCount >= cards.length) {
				onChosen(chosen);
			}
		}

		card.setAttribute('dragged', '');
		card.style.transform = 'perspective(800px) translateX(-50vw) scale(0.8) rotate3d(1, 1, 1, -20deg)';
		cleanupCard(card);
	};

	const cardRight = () => {
		const card = grabCard();
		if (!card) return;

		const val = Number(card.getAttribute('data-value'));
		if (val) {
			chosen.push(val);
			swipeCount++;
			if (swipeCount >= cards.length) {
				onChosen(chosen);
			}
		}

		card.setAttribute('dragged', '');
		card.style.transform = 'perspective(800px) translateX(50vw) scale(0.8) rotate3d(1, 1, 1, 20deg)';
		cleanupCard(card);
	};

	const onTouchStart = (e: TouchEvent) => {
		startX = e.touches[0].clientX;
		startTime = Date.now();
	};

	const onTouchEnd = (e: TouchEvent) => {
		const endX = e.changedTouches[0].clientX;
		const deltaX = endX - startX;
		const deltaTime = Date.now() - startTime;

		if (deltaTime < 500) {
			// Limit for swipe speed
			if (deltaX > 50) {
				cardRight();
			} else if (deltaX < -50) {
				cardLeft();
			}
		}
	};

	const onMouseDown = (e: MouseEvent) => {
		startX = e.clientX;
		startTime = Date.now();
	};

	const onMouseUp = (e: MouseEvent) => {
		const endX = e.clientX;
		const deltaX = endX - startX;
		const deltaTime = Date.now() - startTime;

		if (deltaTime < 500) {
			if (deltaX > 50) {
				cardRight();
			} else if (deltaX < -50) {
				cardLeft();
			}
		}
	};

	document.addEventListener('touchstart', onTouchStart);
	document.addEventListener('touchend', onTouchEnd);
	document.addEventListener('mousedown', onMouseDown);
	document.addEventListener('mouseup', onMouseUp);

	onUnmount(view, () => {
		document.removeEventListener('touchstart', onTouchStart);
		document.removeEventListener('touchend', onTouchEnd);
		document.removeEventListener('mousedown', onMouseDown);
		document.removeEventListener('mouseup', onMouseUp);
	});

	return view;
}
